/**
 * Input — 단일 라인 텍스트 입력 필드
 *
 * label / error / helperText를 래퍼 div로 묶어 제공한다.
 * forwardRef로 외부에서 ref 접근을 지원한다.
 */

import { forwardRef, InputHTMLAttributes } from 'react'
import { cn } from '../../lib/cn.js'
import styles from './Input.module.css'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** 입력 크기. 기본값 md */
  size?: 'sm' | 'md' | 'lg'
  /** 필드 레이블 텍스트 */
  label?: string
  /** 에러 메시지 — 표시 시 입력 테두리가 빨간색으로 변함 */
  error?: string
  /** 보조 안내 텍스트 */
  helperText?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', label, error, helperText, className, id, ...props },
  ref,
) {
  // id가 없으면 label htmlFor 연결이 안 되므로 자동 생성
  const inputId = id ?? (label ? `input-${Math.random().toString(36).slice(2, 9)}` : undefined)

  return (
    <div className={cn(styles.wrapper, error && styles.error, className)}>
      {label && (
        <label className={styles.label} htmlFor={inputId}>
          {label}
        </label>
      )}
      <input
        {...props}
        ref={ref}
        id={inputId}
        className={cn(styles.input, styles[size], error && styles.inputError)}
      />
      {error && <span className={styles.errorText}>{error}</span>}
      {!error && helperText && <span className={styles.helperText}>{helperText}</span>}
    </div>
  )
})
